import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import MultiSelect from "react-multi-select-component";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { DashboardStore, PerformanceThresholds, PeriodTransaction, StoreSelectOption } from "../types";
import { useProductsAndOffers } from "../hooks/useProductsAndOffers";
import {
  aggregateProductData,
  buildProductTimeSeries,
  calculateOfferPerformance,
} from "./ProductsController";
import {
  filterByDateRange,
  filterByStores,
  formatCurrency,
} from "../FinancialDashboardController";
import OfferPerformanceSection from "./OfferPerformanceSection";
import ProductComparisonChart from "./ProductComparisonChart";
import ProductPerformanceTable from "./ProductPerformanceTable";
import "./ProductsDashboard.scss";
import useUIState from "../../../hooks/useUIstate";
import { loadThresholds } from "../Offers/performanceThresholdsUtils";
import LoadingModal from "../../../components/LoadingModal";

interface Props {
  transactions: PeriodTransaction[];
  stores: DashboardStore[];
}

const ProductsDashboard: React.FC<Props> = ({
  transactions,
  stores,
}) => {
  const { t } = useTranslation();
  const { products, offers, loading } = useProductsAndOffers();

  // State
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedStores, setSelectedStores] = useState<StoreSelectOption[]>([]);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const { setToastMessage, setShowToastMessage, setToastType } = useUIState();

  // Store filter options and selected IDs
  const storeOptions = useMemo(
    () => stores.map(s => ({ value: s.StoreId, label: s.Code })),
    [stores],
  );

  const selectedStoreIds = useMemo(
    () => selectedStores.map(s => s.value),
    [selectedStores],
  );

  // Month names for charts
  const monthNames = useMemo(
    () => [
      t("generic.months.january"),
      t("generic.months.february"),
      t("generic.months.march"),
      t("generic.months.april"),
      t("generic.months.may"),
      t("generic.months.june"),
      t("generic.months.july"),
      t("generic.months.august"),
      t("generic.months.september"),
      t("generic.months.october"),
      t("generic.months.november"),
      t("generic.months.december"),
    ],
    [t],
  );

  // Derived state using useMemo
  const storeFilteredTransactions = useMemo(
    () => filterByStores(transactions, selectedStoreIds),
    [transactions, selectedStoreIds],
  );

  const filteredTransactions = useMemo(
    () => filterByDateRange(storeFilteredTransactions, fromDate, toDate),
    [storeFilteredTransactions, fromDate, toDate],
  );

  const productPerformances = useMemo(
    () => aggregateProductData(filteredTransactions, products),
    [filteredTransactions, products],
  );

  const productTimeSeries = useMemo(
    () =>
      buildProductTimeSeries(
        filteredTransactions,
        products,
        monthNames,
      ),
    [filteredTransactions, products, monthNames],
  );

  const [thresholds] = useState<PerformanceThresholds>(loadThresholds());

  const offerPerformances = useMemo(
    () => calculateOfferPerformance(filteredTransactions, offers, products, thresholds),
    [filteredTransactions, offers, products, thresholds],
  );

  // Handlers
  const handleProductSelect = (productCode: string) => {
    if (selectedProducts.includes(productCode)) {
      setSelectedProducts(selectedProducts.filter((p) => p !== productCode));
    } else if (selectedProducts.length < 10) {
      setSelectedProducts([...selectedProducts, productCode]);
    } else {
      setToastMessage(t("productsdashboard.max-products-reached"));
      setToastType("error");
      setShowToastMessage(true);
    }
  };

  // Calculate stats for overview
  const totalProducts = products.length;
  const topPerformer =
    productPerformances.length > 0
      ? productPerformances.reduce((prev, current) =>
          prev.TotalRevenue > current.TotalRevenue ? prev : current,
        )
      : null;
  const avgRevenue =
    productPerformances.length > 0
      ? productPerformances.reduce((sum, p) => sum + p.TotalRevenue, 0) /
        productPerformances.length
      : 0;

  if (loading) {
    return <LoadingModal />;
  }

  return (
    <div className="products-dashboard">
      {/* Filters */}
      <div className="filters-bar">
        <MultiSelect
          className="store-select"
          options={storeOptions}
          value={selectedStores}
          onChange={setSelectedStores}
          labelledBy={t("financialdashboard.select-stores")}
          overrideStrings={{
            selectSomeItems: t("financialdashboard.select-stores"),
            allItemsAreSelected: t("financialdashboard.all-stores"),
          }}
        />

        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <div className="filter-group">
            <label>{t("financialdashboard.from")}</label>
            <DatePicker
              className="datetimepicker-financial"
              clearable
              autoOk
              views={["year", "month"]}
              format="MM/yyyy"
              placeholder="MM/YYYY"
              onChange={(value) => setFromDate(value)}
              value={fromDate}
              animateYearScrolling={false}
            />
          </div>
          <div className="filter-group">
            <label>{t("financialdashboard.to")}</label>
            <DatePicker
              className="datetimepicker-financial"
              clearable
              autoOk
              views={["year", "month"]}
              format="MM/yyyy"
              placeholder="MM/YYYY"
              onChange={(value) => setToDate(value)}
              value={toDate}
              animateYearScrolling={false}
            />
          </div>
        </MuiPickersUtilsProvider>
      </div>

      <div className="date-range-label">
        {t("productsdashboard.showing")}:&nbsp;
        {fromDate || toDate
          ? `${fromDate ? format(fromDate, "MM/yyyy") : "..."} – ${toDate ? format(toDate, "MM/yyyy") : "..."}`
          : t("productsdashboard.all-data")}
      </div>

      {/* Statistics Overview */}
      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-label">
            {t("productsdashboard.stats.total-products")}
          </div>
          <div className="stat-value">{totalProducts}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">
            {t("productsdashboard.stats.top-performer")}
          </div>
          <div className="stat-value">
            {topPerformer?.ProductTitle || "N/A"}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">
            {t("productsdashboard.stats.avg-revenue")}
          </div>
          <div className="stat-value">{formatCurrency(avgRevenue)}</div>
        </div>
      </div>

      {/* Offer Performance Section */}
      <OfferPerformanceSection offers={offerPerformances} />

      {/* Product Performance Table */}
      <ProductPerformanceTable
        performances={productPerformances}
        selectedProducts={selectedProducts}
        onProductSelect={handleProductSelect}
        maxProductsReached={selectedProducts.length >= 10}
        fromDate={fromDate}
        toDate={toDate}
      />

      {/* Product Comparison Chart */}
      <ProductComparisonChart
        selectedProductCodes={selectedProducts}
        timeSeries={productTimeSeries}
      />
    </div>
  );
};

export default ProductsDashboard;
